import React, { useRef, useState } from 'react';
import { autoWhitenBackground } from '../utils/autoWhiteBackground';

/**
 * MediaUpload — drop-in replacement for the image + video upload section in AdminPanel.
 *
 * Props:
 *   images       {string[]}  — array of base64 image strings
 *   video        {string|null}
 *   videoInfo    {object|null}
 *   trimming     {boolean}
 *   onImagesChange(newImages)
 *   onVideoChange(videoB64, videoInfo)
 *   onVideoRemove()
 *   compressImage(file) → Promise<base64>
 *   trimVideo(file)     → Promise<{b64, trimmed, duration}>
 *   MAX_VIDEO_SECONDS   {number}
 */

const MAX_IMAGES = 10;

const MediaUpload = ({
  images = [],
  video = null,
  videoInfo = null,
  trimming = false,
  onImagesChange,
  onVideoChange,
  onVideoRemove,
  compressImage,
  trimVideo,
  MAX_VIDEO_SECONDS = 15,
}) => {
  const imagesRef  = useRef();
  const videoRef   = useRef();
  const dragIdx    = useRef(null);
  const dragOverIdx = useRef(null);

  const [dragActive, setDragActive] = useState(false);
  const [videoDropActive, setVideoDropActive] = useState(false);
  const [localTrimming, setLocalTrimming] = useState(false);
  const [autoClean, setAutoClean] = useState(true);
  const [cleaningIdx, setCleaningIdx] = useState(null);

  const isTrimming = trimming || localTrimming;

  // ── Image upload ─────────────────────────────────────────────
  const handleImageFiles = async (files) => {
    const toAdd = Array.from(files)
      .filter(f => f.type.startsWith('image/'))
      .slice(0, MAX_IMAGES - images.length);
    if (!toAdd.length) return;
    let compressed = await Promise.all(toAdd.map(f => compressImage(f)));
    if (autoClean) {
      // Whiten light/gray studio backgrounds automatically. If cleanup fails
      // for a given photo (e.g. unusual format), fall back to the original.
      compressed = await Promise.all(
        compressed.map(b64 => autoWhitenBackground(b64).catch(() => b64))
      );
    }
    onImagesChange([...images, ...compressed].slice(0, MAX_IMAGES));
  };

  // Retroactively clean a photo that's already in the list (e.g. one
  // uploaded before this feature existed, like the Victus photos).
  const cleanExistingImage = async (idx) => {
    setCleaningIdx(idx);
    try {
      const cleaned = await autoWhitenBackground(images[idx]);
      const next = [...images];
      next[idx] = cleaned;
      onImagesChange(next);
    } catch (err) {
      alert('Could not clean this photo: ' + err.message);
    } finally {
      setCleaningIdx(null);
    }
  };

  const handleImageInput = async (e) => {
    await handleImageFiles(e.target.files);
    e.target.value = '';
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    setDragActive(false);
    await handleImageFiles(e.dataTransfer.files);
  };

  const removeImage = (idx) =>
    onImagesChange(images.filter((_, i) => i !== idx));

  // ── Drag-to-reorder ──────────────────────────────────────────
  const onDragStart = (e, idx) => {
    dragIdx.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDragEnter = (idx) => {
    dragOverIdx.current = idx;
  };

  const onDragEnd = () => {
    const from = dragIdx.current;
    const to   = dragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const reordered = [...images];
    const [moved]   = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    onImagesChange(reordered);
    dragIdx.current     = null;
    dragOverIdx.current = null;
  };

  // ── Video upload ─────────────────────────────────────────────
  const processVideoFile = async (file) => {
    if (!file) return;
    if (file.size > 100 * 1024 * 1024) { alert('Video must be under 100MB'); return; }
    setLocalTrimming(true);
    try {
      const result = await trimVideo(file);
      onVideoChange(result.b64, {
        trimmed: result.trimmed,
        originalDuration: Math.round(result.duration),
        finalDuration: result.trimmed ? MAX_VIDEO_SECONDS : Math.round(result.duration),
      });
      if (result.trimmed) {
        alert(`✂️ Video trimmed to ${MAX_VIDEO_SECONDS}s (was ${Math.round(result.duration)}s)`);
      }
    } catch (err) {
      alert('Could not process video: ' + err.message);
    } finally {
      setLocalTrimming(false);
    }
  };

  const handleVideoInput = async (e) => {
    await processVideoFile(e.target.files[0]);
    e.target.value = '';
  };

  const handleVideoDrop = async (e) => {
    e.preventDefault();
    setVideoDropActive(false);
    const file = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('video/'));
    if (file) await processVideoFile(file);
  };

  const remaining = MAX_IMAGES - images.length;

  return (
    <div style={css.root}>
      <style>{`
        .mu-thumb:hover .mu-remove { opacity: 1 !important; }
        .mu-thumb:hover .mu-drag-handle { opacity: 1 !important; }
        .mu-thumb .mu-remove:disabled { opacity: 1 !important; cursor: wait; }
        .mu-thumb { cursor: grab; }
        .mu-thumb:active { cursor: grabbing; }
        .mu-add-more:hover { border-color: #CC0000 !important; background: rgba(204,0,0,0.08) !important; }
        .mu-video-zone:hover { border-color: rgba(139,92,246,0.7) !important; background: rgba(139,92,246,0.07) !important; }
      `}</style>

      {/* ══ SECTION HEADER ══ */}
      <div style={css.sectionHeader}>
        <div style={css.sectionIcon}>📸</div>
        <div>
          <div style={css.sectionTitle}>Product Media</div>
          <div style={css.sectionSub}>
            Photos + optional video · drag to reorder · first photo = main display image
          </div>
        </div>
        <label style={css.autoCleanToggle} title="Automatically whitens light/gray studio backgrounds on upload">
          <input
            type="checkbox"
            checked={autoClean}
            onChange={(e) => setAutoClean(e.target.checked)}
            style={{ accentColor: '#CC0000' }}
          />
          ✨ Auto-clean background
        </label>
        <div style={css.countPill}>
          {images.length}/{MAX_IMAGES} photos
        </div>
      </div>

      {/* ══ IMAGE DROP ZONE (empty state) ══ */}
      {images.length === 0 && (
        <div
          style={css.emptyDropZone(dragActive)}
          onClick={() => imagesRef.current.click()}
          onDrop={handleImageDrop}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
        >
          <div style={css.emptyIcon}>🖼️</div>
          <div style={css.emptyTitle}>Drop photos here or tap to browse</div>
          <div style={css.emptySub}>JPG · PNG · WEBP &nbsp;·&nbsp; Up to {MAX_IMAGES} photos</div>
          <div style={css.emptyBtn}>Choose Photos</div>
        </div>
      )}

      {/* ══ IMAGE GRID (filled state) ══ */}
      {images.length > 0 && (
        <div
          style={css.gridWrap}
          onDrop={handleImageDrop}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
        >
          {dragActive && images.length < MAX_IMAGES && (
            <div style={css.dropOverlay}>+ Drop to add</div>
          )}

          <div style={css.grid}>
            {images.map((img, idx) => (
              <div
                key={idx}
                className="mu-thumb"
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragEnter={() => onDragEnter(idx)}
                onDragEnd={onDragEnd}
                style={css.thumb(idx === 0)}
              >
                <img src={img} alt={`Product ${idx + 1}`} style={css.thumbImg} />

                {/* Main badge */}
                {idx === 0 && (
                  <div style={css.mainBadge}>⭐ MAIN</div>
                )}

                {/* Index number */}
                {idx > 0 && (
                  <div style={css.indexBadge}>{idx + 1}</div>
                )}

                {/* Drag handle */}
                <div className="mu-drag-handle" style={css.dragHandle} title="Drag to reorder">
                  ⠿
                </div>

                {/* Remove button */}
                <button
                  className="mu-remove"
                  onClick={(e) => { e.stopPropagation(); removeImage(idx); }}
                  style={css.removeBtn}
                  title="Remove"
                >
                  ✕
                </button>

                {/* Clean background button */}
                <button
                  className="mu-remove"
                  onClick={(e) => { e.stopPropagation(); cleanExistingImage(idx); }}
                  style={css.cleanBtn}
                  title="Whiten light/gray background"
                  disabled={cleaningIdx === idx}
                >
                  {cleaningIdx === idx ? '…' : '✨'}
                </button>
              </div>
            ))}

            {/* Add more tile */}
            {images.length < MAX_IMAGES && (
              <div
                className="mu-add-more"
                style={css.addMore}
                onClick={() => imagesRef.current.click()}
                title={`Add up to ${remaining} more`}
              >
                <span style={{ fontSize: '26px' }}>＋</span>
                <span style={css.addMoreLabel}>{remaining} left</span>
              </div>
            )}
          </div>

          {/* Reorder hint */}
          <div style={css.reorderHint}>
            💡 Drag photos to reorder · First photo is shown as the main product image
          </div>
        </div>
      )}

      <input
        ref={imagesRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleImageInput}
        style={{ display: 'none' }}
      />

      {/* ══ VIDEO SECTION ══ */}
      <div style={css.videoSection}>
        <div style={css.videoHeader}>
          <div style={css.videoHeaderLeft}>
            <div style={css.videoIcon}>🎬</div>
            <div>
              <div style={css.videoTitle}>Product Video</div>
              <div style={css.videoSub}>
                Optional · max {MAX_VIDEO_SECONDS}s · longer clips auto-trimmed
              </div>
            </div>
          </div>
          {!video && !isTrimming && (
            <button
              style={css.videoUploadBtn}
              onClick={() => videoRef.current.click()}
            >
              Upload Video
            </button>
          )}
        </div>

        {/* Trimming state */}
        {isTrimming && (
          <div style={css.trimmingBox}>
            <div style={css.trimmingSpinner}>⏳</div>
            <div>
              <div style={css.trimmingTitle}>Processing video…</div>
              <div style={css.trimmingSub}>Trimming to {MAX_VIDEO_SECONDS} seconds, please wait</div>
            </div>
          </div>
        )}

        {/* Video drop zone (no video yet, not trimming) */}
        {!video && !isTrimming && (
          <div
            className="mu-video-zone"
            style={css.videoDropZone(videoDropActive)}
            onClick={() => videoRef.current.click()}
            onDrop={handleVideoDrop}
            onDragOver={(e) => { e.preventDefault(); setVideoDropActive(true); }}
            onDragLeave={() => setVideoDropActive(false)}
          >
            <span style={{ fontSize: '32px' }}>🎥</span>
            <div style={css.videoDropTitle}>Drop a video or tap to browse</div>
            <div style={css.videoDropSub}>MP4 · MOV · WEBM &nbsp;·&nbsp; Under 100MB</div>
          </div>
        )}

        {/* Video preview (video loaded) */}
        {video && !isTrimming && (
          <div style={css.videoPreviewCard}>
            {/* Status bar */}
            <div style={css.videoStatusBar}>
              {videoInfo?.trimmed ? (
                <span style={css.trimmedPill}>
                  ✂️ Trimmed to {MAX_VIDEO_SECONDS}s &nbsp;(was {videoInfo.originalDuration}s)
                </span>
              ) : (
                <span style={css.okPill}>
                  ✅ {videoInfo?.finalDuration}s — Good length
                </span>
              )}
              <button style={css.removeVideoBtn} onClick={onVideoRemove}>
                🗑️ Remove
              </button>
            </div>

            {/* Video player */}
            <video
              src={video}
              controls
              style={css.videoPlayer}
              preload="metadata"
            />

            {/* Replace button */}
            <button
              style={css.replaceVideoBtn}
              onClick={() => videoRef.current.click()}
            >
              🔄 Replace Video
            </button>
          </div>
        )}

        <input
          ref={videoRef}
          type="file"
          accept="video/*"
          onChange={handleVideoInput}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

const css = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },

  // Section header
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: '12px',
    border: '1px solid rgba(255,255,255,0.07)',
  },
  sectionIcon: { fontSize: '24px', flexShrink: 0 },
  sectionTitle: { fontSize: '14px', fontWeight: '800', color: '#fff', letterSpacing: '0.02em' },
  sectionSub:   { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
  countPill: {
    marginLeft: 'auto',
    fontSize: '12px',
    fontWeight: '700',
    color: '#CC0000',
    background: 'rgba(204,0,0,0.12)',
    border: '1px solid rgba(204,0,0,0.25)',
    borderRadius: '100px',
    padding: '4px 12px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },

  // Empty drop zone
  emptyDropZone: (active) => ({
    border: `2px dashed ${active ? '#CC0000' : 'rgba(204,0,0,0.35)'}`,
    borderRadius: '16px',
    backgroundColor: active ? 'rgba(204,0,0,0.08)' : 'rgba(204,0,0,0.03)',
    padding: '40px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  }),
  emptyIcon:  { fontSize: '48px', lineHeight: 1 },
  emptyTitle: { fontSize: '15px', fontWeight: '700', color: '#fff', marginTop: '4px' },
  emptySub:   { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },
  emptyBtn: {
    marginTop: '8px',
    padding: '9px 22px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg,#CC0000,#990000)',
    color: '#fff',
    fontWeight: '700',
    fontSize: '13px',
    border: 'none',
    cursor: 'pointer',
  },

  // Grid
  gridWrap: {
    position: 'relative',
    backgroundColor: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.07)',
    borderRadius: '16px',
    padding: '14px',
  },
  dropOverlay: {
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    backgroundColor: 'rgba(204,0,0,0.15)',
    border: '2px dashed #CC0000',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '800',
    color: '#CC0000',
    zIndex: 5,
    pointerEvents: 'none',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '10px',
  },
  thumb: (isMain) => ({
    position: 'relative',
    aspectRatio: '1',
    borderRadius: '12px',
    overflow: 'hidden',
    border: `2px solid ${isMain ? '#CC0000' : 'rgba(255,255,255,0.1)'}`,
    backgroundColor: '#1a1a1a',
    boxShadow: isMain ? '0 0 0 3px rgba(204,0,0,0.2)' : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
    userSelect: 'none',
  }),
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none',
  },
  mainBadge: {
    position: 'absolute',
    bottom: '6px',
    left: '6px',
    background: '#CC0000',
    color: '#fff',
    fontSize: '9px',
    fontWeight: '900',
    padding: '3px 7px',
    borderRadius: '6px',
    letterSpacing: '0.05em',
    pointerEvents: 'none',
  },
  indexBadge: {
    position: 'absolute',
    bottom: '6px',
    left: '6px',
    background: 'rgba(0,0,0,0.6)',
    color: 'rgba(255,255,255,0.7)',
    fontSize: '10px',
    fontWeight: '700',
    padding: '2px 7px',
    borderRadius: '6px',
    pointerEvents: 'none',
  },
  dragHandle: {
    position: 'absolute',
    top: '6px',
    left: '6px',
    background: 'rgba(0,0,0,0.6)',
    color: 'rgba(255,255,255,0.8)',
    fontSize: '14px',
    width: '22px',
    height: '22px',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.15s',
    pointerEvents: 'none',
    lineHeight: 1,
  },
  removeBtn: {
    position: 'absolute',
    top: '6px',
    right: '6px',
    background: 'rgba(0,0,0,0.7)',
    border: 'none',
    color: '#fff',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.15s',
    lineHeight: 1,
  },
  cleanBtn: {
    position: 'absolute',
    bottom: '6px',
    right: '6px',
    background: 'rgba(204,0,0,0.85)',
    border: 'none',
    color: '#fff',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 1,
    boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
    lineHeight: 1,
  },
  autoCleanToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.6)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
    marginRight: '10px',
  },
  addMore: {
    aspectRatio: '1',
    borderRadius: '12px',
    border: '2px dashed rgba(204,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    gap: '4px',
    backgroundColor: 'rgba(204,0,0,0.03)',
    transition: 'all 0.2s',
    color: 'rgba(255,255,255,0.3)',
  },
  addMoreLabel: {
    fontSize: '10px',
    fontWeight: '700',
    color: 'rgba(255,255,255,0.3)',
    letterSpacing: '0.04em',
  },
  reorderHint: {
    marginTop: '10px',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.25)',
    textAlign: 'center',
  },

  // Video section
  videoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    backgroundColor: 'rgba(139,92,246,0.04)',
    border: '1px solid rgba(139,92,246,0.2)',
    borderRadius: '16px',
    padding: '16px',
  },
  videoHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
  },
  videoHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  videoIcon: { fontSize: '24px', flexShrink: 0 },
  videoTitle: { fontSize: '14px', fontWeight: '800', color: '#fff' },
  videoSub:   { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '2px' },
  videoUploadBtn: {
    padding: '8px 18px',
    borderRadius: '8px',
    border: '1px solid rgba(139,92,246,0.4)',
    background: 'rgba(139,92,246,0.12)',
    color: '#c4b5fd',
    fontWeight: '700',
    fontSize: '13px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
  },

  // Trimming state
  trimmingBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '16px',
    backgroundColor: 'rgba(139,92,246,0.1)',
    border: '1px solid rgba(139,92,246,0.3)',
    borderRadius: '12px',
  },
  trimmingSpinner: { fontSize: '28px', animation: 'spin 1s linear infinite' },
  trimmingTitle:   { fontSize: '14px', fontWeight: '700', color: '#c4b5fd' },
  trimmingSub:     { fontSize: '12px', color: 'rgba(196,181,253,0.6)', marginTop: '2px' },

  // Video drop zone
  videoDropZone: (active) => ({
    border: `2px dashed ${active ? '#8b5cf6' : 'rgba(139,92,246,0.3)'}`,
    borderRadius: '12px',
    backgroundColor: active ? 'rgba(139,92,246,0.1)' : 'transparent',
    padding: '28px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    textAlign: 'center',
  }),
  videoDropTitle: { fontSize: '13px', fontWeight: '700', color: 'rgba(255,255,255,0.7)' },
  videoDropSub:   { fontSize: '11px', color: 'rgba(255,255,255,0.3)' },

  // Video preview card
  videoPreviewCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: '12px',
    padding: '12px',
    border: '1px solid rgba(139,92,246,0.2)',
  },
  videoStatusBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexWrap: 'wrap',
  },
  trimmedPill: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#fbbf24',
    background: 'rgba(234,179,8,0.12)',
    border: '1px solid rgba(234,179,8,0.3)',
    padding: '5px 12px',
    borderRadius: '100px',
  },
  okPill: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#4ade80',
    background: 'rgba(22,163,74,0.12)',
    border: '1px solid rgba(22,163,74,0.3)',
    padding: '5px 12px',
    borderRadius: '100px',
  },
  removeVideoBtn: {
    padding: '5px 14px',
    borderRadius: '8px',
    border: '1px solid rgba(220,38,38,0.3)',
    background: 'rgba(220,38,38,0.1)',
    color: '#f87171',
    fontWeight: '700',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  videoPlayer: {
    width: '100%',
    borderRadius: '10px',
    backgroundColor: '#000',
    maxHeight: '200px',
    objectFit: 'contain',
    display: 'block',
  },
  replaceVideoBtn: {
    alignSelf: 'flex-start',
    padding: '7px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(139,92,246,0.35)',
    background: 'rgba(139,92,246,0.1)',
    color: '#c4b5fd',
    fontWeight: '700',
    fontSize: '12px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default MediaUpload;
